import React, { useState, useCallback } from "react";
import classNames from "classnames";
import { Plus, ChevronUp, ChevronDown, IkonateContext } from "react-ikonate";
import TextInput from "shared/@forms/TextInput";
import SuggestionInput from "shared/@forms/SuggestionInput";
import { Section as LayoutSection } from "shared/layout";
import Button from "shared/Button";
import Card from "shared/Card";

import styles from "./index.module.css";
import { useIBOSearch } from "shared/@hooks/useIBOSearch";
import { Crossbox } from "../../components/Crossbox";
import Section from "./Section";
import Tags from "./Tags";
import { ButtonPurpose } from "shared/Button/Button";
import { useOptionData } from "./useOptionData";
import pageStyles from "../ContentEdit.module.css";

/**
 * Nested category selector that goes three levels deep like the TagMonster.
 */
const formatCategoryLabel = (label) =>
  label.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

function CategorySelector({ categories, content, methods }) {
  const toggleCategory = useCallback(
    (id) => {
      const isEnabled = content.categories.includes(id);

      if (!isEnabled) {
        // Being selected now
        // Recursively select parents
        const cat = categories.find((c) => c.id === id);

        if (cat.parentID && !content.categories.includes(cat.parentID)) {
          toggleCategory(cat.parentID);
        }
      } else {
        // Being deselected now
        // Recursively deselect children
        const children = categories.filter((c) => c.parentID === id);

        for (const child of children) {
          if (content.categories.includes(child.id)) {
            toggleCategory(child.id);
          }
        }
      }

      methods.toggleCategory(id);
    },
    [categories, content]
  );

  const parentCategories = categories.filter((category) => !category.parentID);
  const retailTipsIndex = parentCategories.findIndex(
    (category) => formatCategoryLabel(category.label) === "Retail Tips & Ideas"
  );
  const followUpIndex = parentCategories.findIndex(
    (category) =>
      formatCategoryLabel(category.label) === "Follow Up & Follow Through"
  );

  if (
    retailTipsIndex !== -1 &&
    followUpIndex !== -1 &&
    followUpIndex < retailTipsIndex
  ) {
    const [retailTipsCategory] = parentCategories.splice(retailTipsIndex, 1);
    parentCategories.splice(followUpIndex, 0, retailTipsCategory);
  }

  return (
    <div className={styles.col}>
      {parentCategories.map((category) => (
        <div className={styles.parentCategory} key={category.id}>
          <div className={styles.checkbox}>
            <Crossbox
              label={formatCategoryLabel(category.label)}
              checked={content.categories.includes(category.id)}
              onChange={() => {
                methods.toggleApp(category.applicationID, true);
                toggleCategory(category.id);
              }}
            />
          </div>

          {categories.map((subcategory) => {
            if (subcategory.parentID === category.id) {
              return (
                <div className={styles.subCategory} key={subcategory.id}>
                  <div className={styles.checkbox}>
                    <Crossbox
                      label={formatCategoryLabel(subcategory.label)}
                      checked={content.categories.includes(subcategory.id)}
                      onChange={() => {
                        methods.toggleApp(category.applicationID, true);
                        toggleCategory(subcategory.id);
                      }}
                    />
                  </div>

                  {categories.map((subsubcategory) => {
                    if (subsubcategory.parentID === subcategory.id) {
                      return (
                        <div
                          className={styles.subCategory}
                          key={subsubcategory.id}
                        >
                          <div className={styles.checkbox}>
                            <Crossbox
                              label={formatCategoryLabel(subsubcategory.label)}
                              checked={content.categories.includes(
                                subsubcategory.id
                              )}
                              onChange={() => {
                                methods.toggleApp(category.applicationID, true);
                                toggleCategory(subsubcategory.id);
                              }}
                            />
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              );
            }
          })}
        </div>
      ))}
    </div>
  );
}

export default function ContentSettings({ content, methods, settings }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [editTerm, setEditTerm] = useState("");
  const { searchResults, isLoading } = useIBOSearch(searchTerm);

  const [addCountry, setAddCountry] = useState(null);
  const [addLanguage, setAddLanguage] = useState(null);
  const [addOccupation, setAddOccupation] = useState(null);

  const [addingCountry, setAddingCountry] = useState(false);
  const [addingLanguage, setAddingLanguage] = useState(false);
  const [addingOccupation, setAddingOccupation] = useState(false);
  const [editingOccupation, setEditingOccupation] = useState(false);
  const [deletedOccupationIds, setDeletedOccupationIds] = useState(new Set());
  const [hiddenDeletedOccupationIds, setHiddenDeletedOccupationIds] = useState(
    new Set()
  );
  const [savingOccupations, setSavingOccupations] = useState(false);
  const [occupationDeleteError, setOccupationDeleteError] = useState("");

  const countries = useOptionData("countries");
  const languages = useOptionData("languages");
  const occupations = useOptionData("occupations");

  const toggleOccupationForDeletion = (occupationId) => {
    setDeletedOccupationIds((current) => {
      const next = new Set(current);
      next.has(occupationId)
        ? next.delete(occupationId)
        : next.add(occupationId);
      return next;
    });
    setOccupationDeleteError("");
  };

  const cancelOccupationEdit = () => {
    setEditingOccupation(false);
    setDeletedOccupationIds(new Set());
    setOccupationDeleteError("");
  };

  const saveOccupationEdit = async () => {
    if (deletedOccupationIds.size === 0) {
      setEditingOccupation(false);
      return;
    }

    setSavingOccupations(true);
    const deletedIds = [];
    try {
      for (const occupationId of deletedOccupationIds) {
        await occupations.deleteMutation.mutateAsync(occupationId);
        deletedIds.push(occupationId);
        methods.toggleOccupation(occupationId, false);
      }
      setHiddenDeletedOccupationIds(
        (current) => new Set([...current, ...deletedIds])
      );
      setEditingOccupation(false);
      setDeletedOccupationIds(new Set());
      setOccupationDeleteError("");
    } catch (error) {
      setHiddenDeletedOccupationIds(
        (current) => new Set([...current, ...deletedIds])
      );
      setOccupationDeleteError(
        error?.message || "Failed to delete occupations."
      );
    } finally {
      setSavingOccupations(false);
    }
  };

  if (settings == null) {
    return null;
  }

  return (
    <Card>
      <LayoutSection>
        <header className={classNames(styles.row, styles.header)}>
          <h3 className={styles.sectionTitle}>Content Settings</h3>
          {/* <Button size="small">Collapse All</Button> */}
        </header>

        <div className={styles.permissions}>
          <div className={styles.col}>
            <div className={styles.permissionTitle}>
              {"Membership Permissions"}
            </div>
            <div className={classNames(styles.row, styles.center)}>
              {settings.membershipPermission.map(({ id, label }) => {
                return (
                  <button
                    key={id}
                    className={classNames(styles.circleBtn, {
                      [styles.guestBtn]: id === 1,
                      [styles.basicBtn]: id === 2,
                      [styles.premierBtn]: id === 4,
                      [styles.active]:
                        content.membershipPermissions.includes(id),
                    })}
                    onClick={() => {
                      methods.toggleMembershipPermission(id);
                    }}
                  >
                    {label.charAt(0)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={classNames(styles.col)}>
            <div className={classNames(styles.permissionTitle)}>
              {"Pin Level Permissions"}
            </div>
            <div className={classNames(styles.row)}>
              {settings.pinLevelPermission.map(({ id, label }) => {
                return (
                  <button
                    key={id}
                    className={classNames(styles.circleBtn, {
                      [styles.iboBtn]: id === 1,
                      [styles.platinumBtn]: id === 2,
                      [styles.emeraldBtn]: id === 4,
                      [styles.diamondBtn]: id === 8,
                      [styles.active]: content.pinLevelPermissions.includes(id),
                    })}
                    onClick={() => {
                      methods.togglePinLevelPermission(id);
                    }}
                  >
                    {label.substring(0, 3).toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <Section
          title={
            "Family Reunion Diamond Groups (Will only show to these diamonds and their LOS)"
          }
        >
          <div className={styles.checkboxes}>
            {settings.familyReunionGroups.map((group) => {
              return (
                <div className={styles.checkbox} key={group.id}>
                  <Crossbox
                    label={group.label}
                    checked={content.familyReunionDiamondGroups.includes(
                      group.id
                    )}
                    onChange={() => {
                      methods.toggleFamilyReunionDiamondGroup(group.id);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          title={"Diamonds (Will only show to these diamonds and their LOS)"}
        >
          <div className={styles.checkboxes}>
            {settings.diamonds.map(({ id, label }) => {
              return (
                <div className={styles.checkbox} key={id}>
                  <Crossbox
                    label={label}
                    checked={content.diamonds.includes(id)}
                    onChange={() => {
                      methods.toggleDiamond(id);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          title={
            "Additional IBOs (These IBOs will be added to view content outside of their pin level)"
          }
        >
          <div className={styles.checkboxes}>
            {content.additionalIBOs.map((ibo, index) => {
              return (
                <div className={styles.checkbox} key={index}>
                  <Crossbox
                    label={ibo.label}
                    checked={true}
                    onChange={() => {
                      methods.toggleAdditionalIBO(ibo);
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className={classNames(styles.row, styles.addIBO)}>
            <div className={classNames(styles.addIBOInput)}>
              <SuggestionInput
                value={editTerm}
                autoFocus={false}
                minLength={3}
                debounceTime={500}
                fetchSuggestions={setSearchTerm}
                suggestions={searchResults}
                isLoading={isLoading}
                placeholder="Search for IBO to Add"
                onChange={(e) => {
                  setEditTerm(e.target.value);
                }}
              >
                {(item, closePanel) => {
                  const [, label, rest] = item.display.match(/^(.+):(.*)$/);
                  return (
                    <div key={item.contactPK} className={styles.iboListItem}>
                      <div>
                        <span>{label}:</span>{" "}
                        <button
                          className={classNames(styles.addIBObtn)}
                          onClick={() => {
                            closePanel();
                            setEditTerm("");
                            methods.toggleAdditionalIBO({
                              iboNumber: item.iboNumber,
                              label: `${
                                item.lastName[0].toUpperCase() +
                                item.lastName.slice(1).toLowerCase()
                              } ${item.firstName.charAt(0)}`,
                            });
                          }}
                        >
                          {rest}
                        </button>
                      </div>
                    </div>
                  );
                }}
              </SuggestionInput>
            </div>
          </div>
        </Section>

        <Section title={"Apps"}>
          <div className={styles.checkboxes}>
            {settings.apps.map(({ id, label }) => {
              return (
                <div className={styles.checkbox} key={id}>
                  <Crossbox
                    label={label}
                    checked={content.applications.includes(id)}
                    onChange={() => {
                      methods.toggleApp(id);
                    }}
                  />
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Retail App Categories">
          <CategorySelector
            categories={settings.retail}
            content={content}
            methods={methods}
          />
        </Section>

        <Section title="Simple Selling Framework Categories">
          <CategorySelector
            categories={settings.simpleSellingFramework}
            content={content}
            methods={methods}
          />
        </Section>

        <Section title="Digital Info System Categories">
          <CategorySelector
            categories={settings.digitalInfoSystem}
            content={content}
            methods={methods}
          />
        </Section>

        <Section title={"Tags, etc"}>
          <LayoutSection className={styles.tagsSection}>
            <header>
              <h4>Locations: (If specific to a state/country)</h4>
            </header>

            <div className={classNames(styles.row, styles.locations)}>
              {settings.tags.location.map((location) => {
                return (
                  <div className={styles.location} key={location.id}>
                    <Crossbox
                      label={location.label}
                      checked={content.countries.includes(location.id)}
                      onChange={() => {
                        methods.toggleCountry(location.id);
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {addingCountry && (
              <div className={styles.addBar}>
                <TextInput
                  placeholder={"Add a Country"}
                  value={addCountry}
                  onChange={(e) => setAddCountry(e.target.value)}
                />
                <Button
                  disabled={countries.addMutation.isLoading}
                  onClick={() => {
                    setAddingCountry(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  purpose={ButtonPurpose.save}
                  disabled={countries.addMutation.isLoading}
                  onClick={() => {
                    countries.addMutation
                      .mutateAsync({ country: addCountry })
                      .then(() => {
                        setAddingCountry(!addingCountry);
                      });
                  }}
                >
                  {countries.addMutation.isLoading ? "Adding..." : "Add"}
                </Button>
              </div>
            )}

            {!addingCountry && (
              <button
                onClick={() => {
                  setAddingCountry(!addingCountry);
                }}
                className={styles.add}
              >
                <IkonateContext.Provider value={{ size: 20, border: 1 }}>
                  <Plus />
                </IkonateContext.Provider>
                {"Add"}
              </button>
            )}
          </LayoutSection>

          <LayoutSection>
            <header>
              <h4>Language: (If other than English)</h4>
            </header>

            <div className={classNames(styles.row, styles.languages)}>
              {settings.tags.language.map((language) => {
                return (
                  <div className={styles.language} key={language.id}>
                    <Crossbox
                      label={language.label}
                      checked={content.languages.includes(language.id)}
                      onChange={() => {
                        methods.toggleLanguage(language.id);
                      }}
                    />
                  </div>
                );
              })}

              {addingLanguage && (
                <div className={styles.addBar}>
                  <TextInput
                    placeholder={"Add a Language"}
                    value={addLanguage}
                    onChange={(e) => setAddLanguage(e.target.value)}
                  />
                  <Button
                    disabled={languages.addMutation.isLoading}
                    onClick={() => {
                      setAddingLanguage(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    purpose={ButtonPurpose.save}
                    onClick={() => {
                      languages.addMutation
                        .mutateAsync({ language: addLanguage })
                        .then(() => {
                          setAddingLanguage(!addingLanguage);
                        });
                    }}
                    disabled={languages.addMutation.isLoading}
                  >
                    {languages.addMutation.isLoading ? "Adding..." : "Add"}
                  </Button>
                </div>
              )}
              {!addingLanguage && (
                <button
                  onClick={() => {
                    setAddingLanguage(!addingLanguage);
                  }}
                  className={styles.add}
                >
                  <IkonateContext.Provider value={{ size: 20, border: 1 }}>
                    <Plus />
                  </IkonateContext.Provider>
                  {"Add"}
                </button>
              )}
            </div>
          </LayoutSection>

          <LayoutSection>
            <header className={styles.sectionHeader}>
              <h4>Occupations:</h4>
              {editingOccupation ? (
                <div className={styles.headerButtonGroup}>
                  <Button
                    disabled={savingOccupations}
                    onClick={cancelOccupationEdit}
                    className={pageStyles.cancelButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    purpose={ButtonPurpose.save}
                    disabled={savingOccupations}
                    onClick={() => void saveOccupationEdit()}
                    className={pageStyles.saveButton}
                  >
                    {savingOccupations ? "Saving..." : "Save"}
                  </Button>
                </div>
              ) : (
                <Button
                  size="small"
                  onClick={() => {
                    setAddingOccupation(false);
                    setAddOccupation(null);
                    setEditingOccupation(true);
                  }}
                  className={styles.editButton}
                >
                  Edit
                </Button>
              )}
            </header>

            {occupationDeleteError && (
              <div role="alert" className={styles.deleteError}>
                {occupationDeleteError}
              </div>
            )}

            <div className={classNames(styles.row, styles.languages)}>
              {settings.tags.occupation
                .filter((occ) => !hiddenDeletedOccupationIds.has(occ.id))
                .map((occ) => {
                  const markedForDeletion = deletedOccupationIds.has(occ.id);
                  return (
                    <div
                      className={classNames(
                        styles.location,
                        styles.occupationOption,
                        {
                          [styles.optionMarkedForDeletion]: markedForDeletion,
                        }
                      )}
                      key={occ.id}
                    >
                      <Crossbox
                        label={occ.label}
                        checked={content.occupations.includes(occ.id)}
                        onChange={() => {
                          if (editingOccupation) {
                            toggleOccupationForDeletion(occ.id);
                          } else {
                            methods.toggleOccupation(occ.id);
                          }
                        }}
                      />
                      {editingOccupation && (
                        <button
                          type="button"
                          className={styles.deleteXButton}
                          aria-label={`Mark ${occ.label} for deletion`}
                          aria-pressed={markedForDeletion}
                          onClick={() => toggleOccupationForDeletion(occ.id)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
              {!editingOccupation && addingOccupation && (
                <div className={styles.addBar}>
                  <TextInput
                    placeholder={"Add an Occupation"}
                    value={addOccupation}
                    onChange={(e) => setAddOccupation(e.target.value)}
                  />
                  <Button
                    disabled={occupations.addMutation.isLoading}
                    onClick={() => {
                      setAddingOccupation(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    purpose={ButtonPurpose.save}
                    onClick={() => {
                      occupations.addMutation
                        .mutateAsync({ occupation: addOccupation })
                        .then(() => {
                          setAddingOccupation(!addingOccupation);
                          setAddOccupation(null);
                        });
                    }}
                    disabled={occupations.addMutation.isLoading}
                  >
                    {occupations.addMutation.isLoading ? "Adding..." : "Add"}
                  </Button>
                </div>
              )}
              {!editingOccupation && !addingOccupation && (
                <button
                  onClick={() => {
                    setAddingOccupation(!addingOccupation);
                  }}
                  className={styles.add}
                >
                  <IkonateContext.Provider value={{ size: 20, border: 1 }}>
                    <Plus />
                  </IkonateContext.Provider>
                  {"Add"}
                </button>
              )}
            </div>
          </LayoutSection>

          {/* <div className={classNames(styles.row, styles.author)}>
            {"IBO: (Author / Contributor ) 0 selected"}

            <button
              className={classNames(styles.authorButton)}
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open && (
                <IkonateContext.Provider value={{ size: 30, border: 1 }}>
                  <ChevronUp />
                </IkonateContext.Provider>
              )}
              {!open && (
                <IkonateContext.Provider value={{ size: 30, border: 1 }}>
                  <ChevronDown />
                </IkonateContext.Provider>
              )}
            </button>
          </div> */}

          <Tags
            tags={content.tags}
            setTags={methods.setTags}
            keywordTags={settings.tags.tags}
            hiddenTags={settings.tags.hiddenTags}
            internalTags={settings.tags.internalTags}
          />
        </Section>
      </LayoutSection>
    </Card>
  );
}
